import { useLocation } from 'react-router-dom';
import LandingLayout from './LandingLayout';
import { useI18n } from '../../i18n/LanguageContext';
import { resolveLandingContext } from './regional';

type PageContent = {
  title: string;
  subtitle: string;
  sectionTitle: string;
  sectionBody: string;
  sectionDetail: string;
  imageAlt: string;
  outcomesTitle: string;
  outcomes: Array<{ title: string; body: string }>;
};

const CONTENT: Record<'default' | 'es', PageContent> = {
  default: {
    title: 'AI EMR Software',
    subtitle: 'An EMR designed for real clinical execution with intelligent support in every workflow.',
    sectionTitle: 'Chart Context with Embedded AI',
    sectionBody: 'Each chart includes longitudinal summaries, risk factors, pending tasks, and next-best actions for teams.',
    sectionDetail: 'Information is presented in actionable form to reduce friction between consultation and documentation.',
    imageAlt: 'Patient chart with AI summary',
    outcomesTitle: 'Expected Operational Outcomes',
    outcomes: [
      { title: 'Lower Manual Load', body: 'Automates repetitive documentation and follow-up tasks.' },
      { title: 'Better Clinical Continuity', body: 'Connects consultation, lab, and communication data in one operational view.' },
      { title: 'Faster Decision Velocity', body: 'Prioritizes risks and unresolved items for faster clinical decisions.' },
    ],
  },
  es: {
    title: 'Software EMR con IA en Espanol',
    subtitle: 'EMR disenado para clinicas y hospitales que requieren velocidad, continuidad y control operativo en un entorno de trabajo en espanol.',
    sectionTitle: 'Expediente Clinico Inteligente para Equipos Hispanohablantes',
    sectionBody: 'Cada expediente integra resumen longitudinal, riesgo clinico, tareas pendientes y recomendaciones para decisiones asistenciales.',
    sectionDetail: 'La informacion se muestra de forma accionable para reducir friccion entre consulta, seguimiento y documentacion.',
    imageAlt: 'Expediente clinico con IA en espanol',
    outcomesTitle: 'Resultados Esperados en la Operacion',
    outcomes: [
      { title: 'Menos Carga Administrativa', body: 'Automatiza tareas repetitivas de documentacion y seguimiento.' },
      { title: 'Continuidad de Cuidado', body: 'Conecta consulta, laboratorio y comunicacion en una sola vista operativa.' },
      { title: 'Mejor Velocidad de Decision', body: 'Prioriza riesgos y pendientes para responder mas rapido en operacion diaria.' },
    ],
  },
};

export default function AiEmrPage() {
  const location = useLocation();
  const { language } = useI18n();
  const { market } = resolveLandingContext(location.pathname, language);
  const content = CONTENT[market] ?? CONTENT.default;

  return (
    <LandingLayout pageKey="ai-emr">
      <section className="tech-header">
        <div className="container">
          <h1 className="reveal-text">{content.title}</h1>
          <p className="tech-features-intro">{content.subtitle}</p>
        </div>
      </section>

      <section className="deep-dive">
        <div className="container deep-dive-grid">
          <div className="dd-content">
            <h2>{content.sectionTitle}</h2>
            <p>{content.sectionBody}</p>
            <p className="tech-feature-detail">{content.sectionDetail}</p>
          </div>
          <div className="dd-image">
            <img src="/emr/patient-file-ai-summary.png" alt={content.imageAlt} loading="lazy" />
          </div>
        </div>
      </section>

      <section className="features">
        <div className="container">
          <h2>{content.outcomesTitle}</h2>
          <div className="features-grid">
            {content.outcomes.map((item) => (
              <div key={item.title} className="feature-item">
                <h3>{item.title}</h3>
                <p>{item.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>
    </LandingLayout>
  );
}
